/**
 * Reusable skeleton loading primitives for progressive page rendering.
 * Use instead of spinners to show page structure during data loading.
 */

export const Shimmer = ({ className = "", style }) => (
  <div className={`animate-pulse bg-white/[0.04] rounded-lg ${className}`} style={style} />
);

export const DashboardSkeleton = ({ galaxyColor = "violet" }) => {
  const colors = {
    violet: { badge: "text-violet-400", accent: "border-violet-500/20" },
    amber: { badge: "text-amber-400", accent: "border-amber-500/20" },
    cyan: { badge: "text-cyan-400", accent: "border-cyan-500/20" },
    emerald: { badge: "text-emerald-400", accent: "border-emerald-500/20" },
  };
  const c = colors[galaxyColor] || colors.violet;

  return (
    <div className="space-y-6 animate-fade-in pb-8" data-testid="dashboard-skeleton">
      {/* Hero skeleton */}
      <div className="relative rounded-2xl overflow-hidden bg-white/[0.02] border border-white/[0.04]">
        <div className="px-8 py-10 text-center space-y-3">
          <Shimmer className={`h-3 w-36 mx-auto ${c.accent}`} />
          <Shimmer className="h-9 w-72 mx-auto" />
          <Shimmer className="h-4 w-96 mx-auto" />
          <Shimmer className="h-14 w-40 mx-auto rounded-full mt-3" />
        </div>
      </div>
      {/* Stats row skeleton */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 space-y-2">
            <Shimmer className="h-2.5 w-16" />
            <Shimmer className="h-8 w-20" />
            <Shimmer className="h-2 w-24" />
          </div>
        ))}
      </div>
      {/* Charts skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {[1, 2].map(i => (
          <div key={i} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
            <Shimmer className="h-3 w-40 mb-4" />
            <div className="h-64 flex items-end gap-3 px-4">
              {[70, 55, 48, 65, 40, 35, 50].map((h, j) => (
                <Shimmer key={j} className="flex-1" style={{ height: `${h}%` }} />
              ))}
            </div>
          </div>
        ))}
      </div>
      {/* Table skeleton */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-white/[0.06]">
          <Shimmer className="h-4 w-48" />
        </div>
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="flex items-center gap-4 px-5 py-3 border-b border-white/[0.03]">
            <Shimmer className="w-6 h-4" />
            <div className="flex-1 space-y-1.5">
              <Shimmer className="h-3.5 w-36" />
              <Shimmer className="h-2.5 w-52" />
            </div>
            <Shimmer className="w-12 h-6" />
            <Shimmer className="w-10 h-6" />
          </div>
        ))}
      </div>
    </div>
  );
};

export default DashboardSkeleton;
